<template>
    <section class="ip-notice mb-3"
        :class="{ 'is-blocked': blocked }"
        :role="blocked ? 'alert' : 'note'"
        aria-labelledby="ip-notice-title">
        <div class="ip-notice__stripe"></div>

        <div class="ip-notice__icon-ring" aria-hidden="true">
            <fa :icon="blocked ? 'exclamation-triangle' : 'server'" />
        </div>

        <div class="ip-notice__body">
            <span class="ip-notice__badge" v-if="blocked">
                <span class="ip-notice__badge-dot"></span>
                Erro 403 detectado
            </span>

            <h3 id="ip-notice-title" class="ip-notice__title" tabindex="-1" ref="title">
                {{ blocked ? 'Sua hospedagem bloqueou o acesso do Muven' : 'Sua hospedagem precisa aceitar conexões de fora do Brasil' }}
            </h3>

            <p class="ip-notice__lead" v-if="blocked">
                As credenciais podem estar certas: o erro 403 significa que o servidor da loja <strong>recusou a conexão</strong> antes de verificá-las.
                Isso acontece quando a hospedagem bloqueia acessos internacionais.
            </p>
            <p class="ip-notice__lead" v-else>
                O Muven acessa a API do seu WooCommerce a partir de servidores na nuvem, <strong>fora do Brasil e sem IP fixo</strong>.
                Se a hospedagem bloquear acessos internacionais, o teste de conexão retorna erro 403.
            </p>

            <details class="ip-notice__details" :open="detailsOpen" @toggle="detailsOpen = $event.target.open">
                <summary class="ip-notice__summary">
                    <fa icon="chevron-right" class="ip-notice__summary-chevron" aria-hidden="true" />
                    Como resolver
                </summary>

                <div class="ip-notice__flow" aria-label="Quem conecta em quem">
                    <div class="ip-notice__node">
                        <fa icon="server" class="ip-notice__node-icon" aria-hidden="true" />
                        <span class="ip-notice__node-name">Muven</span>
                        <span class="ip-notice__node-sub">Nuvem, fora do Brasil, sem IP fixo</span>
                    </div>

                    <fa icon="chevron-right" class="ip-notice__flow-arrow" aria-hidden="true" />

                    <div class="ip-notice__node ip-notice__node--gate">
                        <fa :icon="blocked ? 'ban' : 'lock'" class="ip-notice__node-icon" aria-hidden="true" />
                        <span class="ip-notice__node-name">Firewall da hospedagem</span>
                        <span class="ip-notice__node-sub">{{ blocked ? 'Bloqueando com 403' : 'Precisa liberar' }}</span>
                    </div>

                    <fa icon="chevron-right" class="ip-notice__flow-arrow ip-notice__flow-arrow--after-gate" aria-hidden="true" />

                    <div class="ip-notice__node">
                        <fa icon="store-alt" class="ip-notice__node-icon" aria-hidden="true" />
                        <span class="ip-notice__node-name">API WooCommerce</span>
                        <span class="ip-notice__node-sub">/wp-json/wc/v3</span>
                    </div>
                </div>

                <p class="ip-notice__text">
                    É o Muven que se conecta à sua loja, não o contrário. Por isso não há nada a liberar do lado do Muven:
                    a liberação é feita no servidor da loja. Hospedagens podem ativar esse bloqueio sem aviso, então
                    uma integração que funcionava pode parar de um dia para o outro.
                </p>

                <p class="ip-notice__subtitle">O que pedir à hospedagem ou à equipe de TI</p>
                <ol class="ip-notice__steps">
                    <li>Liberar acesso de <strong>qualquer país</strong> ao caminho <code>/wp-json/wc/v3/</code>.</li>
                    <li>Desativar o geobloqueio ou a regra de firewall/WAF que barra esse caminho (ex.: ModSecurity, Cloudflare, Wordfence).</li>
                    <li>Não é possível liberar por IP, porque o Muven <strong>não possui IP fixo</strong>. A API continua protegida pela Consumer key e Consumer secret.</li>
                </ol>

                <div class="ip-notice__actions">
                    <button type="button"
                        class="btn btn-sm"
                        :class="blocked ? 'btn-primary' : 'btn-outline-primary'"
                        @click="copyMessage()">
                        <fa :icon="copied ? 'check-circle' : ['far', 'copy']" class="mr-1" />
                        {{ copied ? 'Mensagem copiada' : 'Copiar mensagem para a hospedagem' }}
                    </button>

                    <a :href="WOOCOMMERCE_DOCS_URL" target="_blank" rel="noopener" class="ip-notice__link">
                        Documentação da API WooCommerce
                        <fa icon="external-link-alt" class="ml-1" />
                    </a>

                    <span class="sr-only" aria-live="polite">{{ copied ? 'Mensagem copiada para a área de transferência' : '' }}</span>
                </div>
            </details>
        </div>
    </section>
</template>

<script>
const WOOCOMMERCE_DOCS_URL = 'https://developer.woocommerce.com/docs/apis/rest-api/v3/'
const COPIED_FEEDBACK_MS = 2500

export default {
    name: 'WooCommerceIpBlockNoticeCmp',
    props: {
        blocked: {
            type: Boolean,
            default: false,
        },
        storeUrl: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            WOOCOMMERCE_DOCS_URL,
            detailsOpen: false,
            copied: false,
            copiedTimeout: null,
        }
    },
    watch: {
        blocked(isBlocked) {
            if (!isBlocked) {
                return
            }

            this.detailsOpen = true

            this.$nextTick(() => {
                const title = this.$refs.title
                title.scrollIntoView({ behavior: 'smooth', block: 'center' })
                title.focus({ preventScroll: true })
            })
        },
    },
    computed: {
        hostingMessage() {
            const store = this.storeUrl ? ` (${this.storeUrl.trim()})` : ''

            return [
                'Olá, equipe de hospedagem/TI.',
                '',
                `Nossa loja WooCommerce${store} está integrada ao Muven, que acessa a API REST do WooCommerce a partir de servidores em nuvem localizados fora do Brasil e sem IP fixo.`,
                '',
                'As requisições estão sendo recusadas com erro 403 (Forbidden), o que indica bloqueio por firewall, WAF ou geobloqueio no servidor.',
                '',
                'Por favor:',
                '1. Liberem o acesso de qualquer país ao caminho /wp-json/wc/v3/ (API REST do WooCommerce).',
                '2. Desativem o geobloqueio ou a regra de firewall/WAF que esteja barrando esse caminho (ex.: ModSecurity, Cloudflare, Wordfence).',
                '3. Mantenham a autenticação por Consumer key e Consumer secret, que já protege a API.',
                '',
                'Não é possível liberar por IP específico, pois o Muven não possui IP fixo.',
                `Documentação da API: ${WOOCOMMERCE_DOCS_URL}`,
                '',
                'Obrigado!',
            ].join('\n')
        },
    },
    methods: {
        async copyMessage() {
            try {
                await navigator.clipboard.writeText(this.hostingMessage)
            } catch (e) {
                this.copyWithTextarea(this.hostingMessage)
            }

            this.copied = true
            clearTimeout(this.copiedTimeout)
            this.copiedTimeout = setTimeout(() => {
                this.copied = false
            }, COPIED_FEEDBACK_MS)
        },
        // Fallback para contexto sem Clipboard API (http ou navegador antigo)
        copyWithTextarea(text) {
            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.setAttribute('readonly', '')
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
        },
    },
    beforeDestroy() {
        clearTimeout(this.copiedTimeout)
    },
}
</script>

<style lang="scss" scoped>
.ip-notice {
    --tone-bg: var(--info-bg);
    --tone-border: var(--info-border);
    --tone-fg: var(--info-fg);
    --tone-solid: var(--info-solid);

    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px 18px 16px 22px;
    background: var(--tone-bg);
    border: 1px solid var(--tone-border);
    border-radius: 10px;
    overflow: hidden;
    transition: background-color .3s ease, border-color .3s ease;
    animation: ipNoticeIn .35s ease;

    &.is-blocked {
        --tone-bg: var(--warning-bg);
        --tone-border: var(--warning-border);
        --tone-fg: var(--warning-fg);
        --tone-solid: var(--warning-solid);

        box-shadow: 0 2px 10px color-mix(in srgb, var(--warning-fg) 12%, transparent);
        animation: ipNoticeIn .35s ease, ipNoticeNudge .5s ease .35s;
    }
}

@keyframes ipNoticeIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
}

@keyframes ipNoticeNudge {
    0%, 100% { transform: translateX(0); }
    25%      { transform: translateX(-4px); }
    75%      { transform: translateX(4px); }
}

.ip-notice__stripe {
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--tone-solid);
}

.ip-notice__icon-ring {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1.5px solid var(--tone-border);
    background: var(--surface-1);
    color: var(--tone-fg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
}

.ip-notice__body {
    flex: 1;
    min-width: 0;
}

.ip-notice__badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding: 2px 10px;
    border: 1px solid var(--tone-border);
    border-radius: 20px;
    background: var(--surface-1);
    color: var(--tone-fg);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
}

.ip-notice__badge-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--tone-solid);
    animation: ipNoticePulse 1.8s ease-in-out infinite;
}

@keyframes ipNoticePulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%      { opacity: .5; transform: scale(.75); }
}

.ip-notice__title {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    color: var(--tone-fg);

    &:focus {
        outline: none;
    }
}

.ip-notice__lead,
.ip-notice__text {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: var(--text-soft);

    strong {
        color: var(--text-strong);
    }
}

.ip-notice__details {
    margin-top: 10px;
}

.ip-notice__summary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    list-style: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: var(--tone-fg);
    user-select: none;

    &::-webkit-details-marker {
        display: none;
    }

    &:hover {
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 2px solid var(--focus-ring);
        outline-offset: 2px;
        border-radius: 3px;
    }
}

.ip-notice__summary-chevron {
    font-size: 10px;
    transition: transform .2s ease;
}

.ip-notice__details[open] .ip-notice__summary-chevron {
    transform: rotate(90deg);
}

.ip-notice__details[open] > :not(summary) {
    animation: ipNoticeIn .25s ease;
}

.ip-notice__flow {
    display: flex;
    align-items: stretch;
    gap: 6px;
    margin: 12px 0;
}

.ip-notice__node {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 10px 6px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    background: var(--surface-1);
    text-align: center;
}

.ip-notice__node-icon {
    margin-bottom: 4px;
    font-size: 16px;
    color: var(--text-muted);
}

.ip-notice__node-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-strong);
}

.ip-notice__node-sub {
    font-size: 11px;
    line-height: 1.3;
    color: var(--text-muted);
}

.ip-notice__node--gate {
    border-style: dashed;
    border-color: var(--tone-border);

    .ip-notice__node-icon,
    .ip-notice__node-sub {
        color: var(--tone-fg);
    }
}

.is-blocked .ip-notice__node--gate {
    border-style: solid;
    border-color: var(--danger-border);
    background: var(--danger-bg);

    .ip-notice__node-icon,
    .ip-notice__node-sub {
        color: var(--danger-fg);
    }
}

.ip-notice__flow-arrow {
    align-self: center;
    flex-shrink: 0;
    font-size: 11px;
    color: var(--text-faint);
}

.is-blocked .ip-notice__flow-arrow--after-gate {
    opacity: .3;
}

.ip-notice__subtitle {
    margin: 12px 0 4px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-strong);
}

.ip-notice__steps {
    margin: 0 0 12px;
    padding-left: 20px;
    font-size: 13px;
    line-height: 1.6;
    color: var(--text-soft);

    li + li {
        margin-top: 2px;
    }

    code {
        padding: 1px 5px;
        border-radius: 4px;
        background: var(--surface-1);
        color: var(--text-strong);
        font-size: 12px;
    }
}

.ip-notice__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
}

.ip-notice__link {
    font-size: 13px;
}

@media (max-width: 576px) {
    .ip-notice {
        padding: 14px 14px 14px 18px;
    }

    .ip-notice__icon-ring {
        display: none;
    }

    .ip-notice__flow {
        flex-direction: column;
    }

    .ip-notice__flow-arrow {
        transform: rotate(90deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .ip-notice,
    .ip-notice.is-blocked,
    .ip-notice__badge-dot,
    .ip-notice__details[open] > :not(summary) {
        animation: none;
    }

    .ip-notice__summary-chevron {
        transition: none;
    }
}
</style>
